import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  fetchCommonAreas as fetchCommonAreasApi,
  fetchPropertyCommonAreas as fetchPropertyCommonAreasApi,
  savePropertyCommonAreas as savePropertyCommonAreasApi,
} from 'src/services/client';
import { fetchPropertyCounts } from 'src/store/actions/properties';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';

export interface CommonArea {
  id: any;
  name: string;
  type?: string;
  customLabel?: string;
  new?: boolean;
  [key: string]: any;
}

export interface PropertyCommonAreasShape {
  commonAreas: CommonArea[];
  otherCommonAreas: CommonArea[];
}

export interface CommonAreaSavePayload {
  PropertyId: any;
  UserId: any;
  CommonAreas: CommonArea[];
}

export interface UseCommonAreasReturn {
  commonAreas: CommonArea[];
  propertyCommonAreas: PropertyCommonAreasShape;
  filteredPropertyAreas: CommonArea[];
  loading: boolean;
  isSaving: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  refetch: () => Promise<void>;
  saveCommonAreas: (payload: CommonAreaSavePayload) => Promise<boolean>;
}

const EMPTY_PROPERTY_AREAS: PropertyCommonAreasShape = {
  commonAreas: [],
  otherCommonAreas: [],
};

const parseResponse = async (response: any): Promise<any> => {
  if (!response) {
    return null;
  }

  if (response.ok || response.status === 200) {
    const json: any = await response.json();
    return json?.data ?? null;
  }

  return null;
};

export const useCommonAreas = (propertyId?: any): UseCommonAreasReturn => {
  const dispatch: any = useDispatch();
  const user: any = useSelector(selectAuthenticatedUser);

  const [commonAreas, setCommonAreas] = useState<CommonArea[]>([]);
  const [propertyCommonAreas, setPropertyCommonAreas] =
    useState<PropertyCommonAreasShape>(EMPTY_PROPERTY_AREAS);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const fetchAll = async (): Promise<void> => {
    if (!propertyId) {
      return;
    }

    setLoading(true);

    try {
      const [catalogRes, propertyRes]: any[] = await Promise.all([
        fetchCommonAreasApi(),
        fetchPropertyCommonAreasApi(propertyId),
      ]);

      const catalogData: any = await parseResponse(catalogRes);
      const propertyData: any = await parseResponse(propertyRes);

      setCommonAreas(Array.isArray(catalogData) ? catalogData : []);
      setPropertyCommonAreas(
        propertyData && typeof propertyData === 'object'
          ? {
              commonAreas: propertyData.commonAreas ?? [],
              otherCommonAreas: propertyData.otherCommonAreas ?? [],
            }
          : EMPTY_PROPERTY_AREAS
      );
    } catch (err) {
      console.error('useCommonAreas fetch error:', err);
      setCommonAreas([]);
      setPropertyCommonAreas(EMPTY_PROPERTY_AREAS);
    } finally {
      setLoading(false);
    }
  };

  const saveCommonAreas = async (
    payload: CommonAreaSavePayload
  ): Promise<boolean> => {
    setIsSaving(true);
    try {
      const response: any = await savePropertyCommonAreasApi(payload);

      if (response?.ok || response?.status === 200) {
        const json: any = await response.json();

        toast.success('  ⓘ Common Areas Configured Successfully.', {
          autoClose: 10000,
          position: 'top-center',
        });

        await fetchAll();

        if (user?.id && user?.roles?.[0]?.role?.name) {
          dispatch(
            fetchPropertyCounts(
              json?.data?.propertyId ?? payload.PropertyId,
              user.id,
              user.roles[0].role.name
            )
          );
        }

        return true;
      }

      toast.error('🚫 Something Went Wrong Please try again.', {
        autoClose: 10000,
        position: 'top-center',
      });

      return false;
    } catch (err) {
      console.error('useCommonAreas save error:', err);

      toast.error('🚫 Something Went Wrong Please try again.', {
        autoClose: 10000,
        position: 'top-center',
      });

      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const filteredPropertyAreas: CommonArea[] = useMemo(() => {
    const term: string = searchTerm.trim().toLowerCase();
    const areas: CommonArea[] = propertyCommonAreas.commonAreas ?? [];

    if (!term) {
      return areas;
    }

    return areas.filter((a: CommonArea) =>
      (a.customLabel || a.name || '').toLowerCase().includes(term)
    );
  }, [propertyCommonAreas, searchTerm]);

  useEffect(() => {
    if (!propertyId) {
      return;
    }

    void fetchAll();
  }, [propertyId]);

  return {
    commonAreas,
    propertyCommonAreas,
    filteredPropertyAreas,
    loading,
    isSaving,
    searchTerm,
    setSearchTerm,
    refetch: fetchAll,
    saveCommonAreas,
  };
};

export default useCommonAreas;
